// Marking the answers an English Skill Builder could not mark itself: the questions the author
// flagged, and the types with no answer to check against.
//
// Reached from the Skill Builder card on the lesson plan, which nags until the list is empty.
//
// A near-twin of eng-diag-marking, deliberately - a tutor moves between the two and a page that
// reads differently looks like a different job. Two differences, both real:
//
//   The heading names the SKILL, because a Skill Builder is one skill, where a diagnostic spans
//   a year level and names that instead.
//
//   Marking here earns NOTHING downstream. A wrong verdict on a diagnostic extends the
//   prerequisite chain; a Skill Builder is already the remediation, so the copy must not promise
//   that more work will appear.

import React, { useCallback, useEffect, useState } from "react"
import { Container, Spinner } from "reactstrap"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import {
  getEngSkillBuilderAnswersAwaitingMarking,
  markEngSkillBuilderAnswer,
  type EngDiagMisconceptionType,
  type EngSkillBuilderAnswerToMark,
} from "../../modules/eng-diag/services/eng-diag.service"

const INK = "#1f2a44"
const MUTED = "#5f7191"
const ACCENT = "#c62644"

const CARD: React.CSSProperties = {
  backgroundColor: "#fff",
  border: "1px solid #e9edf3",
  borderRadius: 10,
  padding: 20,
}

/**
 * The student's answer to the part being marked, and what the author would have accepted.
 *
 * Same shape as the diagnostics page, including the reading_comp branch: the AQ bank has the
 * same open question types, so a sub-question is marked on its own there too.
 */
function answerDetail(answer: EngSkillBuilderAnswerToMark): {
  prompt: string | null
  given: string
  accepted: string[]
} {
  const options = answer.question.options || {}
  const response = answer.response || {}

  if (answer.question.type === "reading_comp") {
    const sub = (options.subQuestions || []).find((s: any) => s.id === answer.target_ref)
    return {
      prompt: sub?.prompt ?? null,
      given: response[answer.target_ref] ?? "",
      accepted: sub?.answers ?? [],
    }
  }
  const entries = Object.entries(response)
  return {
    prompt: null,
    given: entries
      .map(([key, value]) => (entries.length > 1 ? `${key}: ${value}` : value))
      .join("  ·  "),
    accepted: [],
  }
}

const Html: React.FC<{ html?: string; className?: string }> = ({ html, className }) =>
  html ? <div className={className} dangerouslySetInnerHTML={{ __html: html }} /> : null

export const EngSkillBuilderMarkingPage: React.FC = () => {
  const { key } = useParams<{ key: string }>()
  const navigate = useNavigate()
  const activityKey = key || ""

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [answers, setAnswers] = useState<EngSkillBuilderAnswerToMark[]>([])
  const [types, setTypes] = useState<EngDiagMisconceptionType[]>([])
  const [skillName, setSkillName] = useState<string>("")
  const [index, setIndex] = useState(0)

  /** Reset per answer: a verdict must be chosen deliberately, not inherited from the last one. */
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null)
  const [misconception, setMisconception] = useState<string>("")

  const load = useCallback(async (signal?: AbortSignal) => {
    const data = await getEngSkillBuilderAnswersAwaitingMarking(
      { pk_activity_key: activityKey },
      signal,
    )
    if (data.error) throw new Error(data.error)
    setAnswers(data.answers)
    setTypes(data.misconception_types)
    setSkillName(data.skill_name || "")
  }, [activityKey])

  useEffect(() => {
    const controller = new AbortController()
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        await load(controller.signal)
      } catch (e: any) {
        if (!controller.signal.aborted) {
          setError(e?.message || "Unable to load this Skill Builder.")
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    })()
    return () => controller.abort()
  }, [load])

  const current = answers[index] || null

  const save = async () => {
    if (!current || isCorrect === null || saving) return
    setSaving(true)
    try {
      const result = await markEngSkillBuilderAnswer({
        pk_activity_key: activityKey,
        attempt_key: current.pk_attempt_key,
        is_correct: isCorrect,
        ...(isCorrect || !misconception ? {} : { misconception_type_code: misconception }),
      })
      if (result.status !== 1) throw new Error(result.error || "Unable to save that mark.")

      // Drop it from the list rather than refetching: the tutor keeps their place.
      setAnswers(prev => prev.filter(a => a.pk_attempt_key !== current.pk_attempt_key))
      setIndex(prev => Math.min(prev, Math.max(0, answers.length - 2)))
      setIsCorrect(null)
      setMisconception("")
    } catch (e: any) {
      toast.error(e?.message || "Unable to save that mark.", { autoClose: 3000 })
    } finally {
      setSaving(false)
    }
  }

  const header = (
    <div className="cs-pg-header bg-dark-blue py-4">
      <Container>
        <div className="d-flex align-items-center">
          <div className="me-4">
            <div
              className="text-white font-size-14 text-uppercase"
              style={{ cursor: "pointer" }}
              onClick={() => navigate(-1)}
              role="button"
            >
              <i className="fas fa-chevron-left me-1"></i> Back
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-between flex-grow-1">
            <h6 className="mb-0 font-size-20 text-white fw-regular">Mark answers</h6>
            <p className="mb-0 text-uppercase luminous-wht">
              <span className="text-yellow me-1">English</span>
              Skill Builder
            </p>
          </div>
        </div>
      </Container>
    </div>
  )

  const shell = (children: React.ReactNode) => (
    <div className="page-content">
      {header}
      <Container className="py-4">{children}</Container>
    </div>
  )

  if (loading) {
    return shell(
      <div className="text-center py-5">
        <Spinner color="primary" />
      </div>,
    )
  }
  if (error) return shell(<div className="alert alert-warning font-size-13">{error}</div>)

  if (!current) {
    return shell(
      <div style={CARD}>
        <h5 style={{ color: INK }} className="fw-700 mb-2">Nothing left to mark</h5>
        <p style={{ color: MUTED }} className="mb-0">
          {/* No promise of further work: unlike a diagnostic, a Skill Builder earns nothing
              from a wrong verdict. It is already the remediation. */}
          Every answer in this Skill Builder has been marked.
        </p>
      </div>,
    )
  }

  const detail = answerDetail(current)

  return shell(
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span style={{ color: MUTED, fontSize: 13 }}>
          {answers.length} answer{answers.length === 1 ? "" : "s"} left to mark
        </span>
        {skillName && (
          <span style={{ color: MUTED, fontSize: 13 }}>Practising: {skillName}</span>
        )}
      </div>

      <div style={{ ...CARD, marginBottom: 16 }}>
        <Html html={current.question.instruction} className="mb-2" />
        <Html html={current.question.content} />
        {detail.prompt && (
          <p className="mt-3 mb-0 fw-600" style={{ color: INK }}>{detail.prompt}</p>
        )}
      </div>

      <div style={{ ...CARD, marginBottom: 16 }}>
        <div style={{ color: MUTED, fontSize: 12.5, fontWeight: 700 }}>The student wrote</div>
        <p className="mb-0 mt-1 font-size-16" style={{ color: INK }}>
          {detail.given.trim() ? detail.given : <em style={{ color: MUTED }}>Nothing entered</em>}
        </p>
        {detail.accepted.length > 0 && (
          <div className="mt-3" style={{ color: MUTED, fontSize: 12.5 }}>
            Accepted answers: {detail.accepted.join(", ")}
          </div>
        )}
      </div>

      <div style={CARD}>
        <div className="d-flex gap-2 mb-3">
          <button
            type="button"
            className="btn fw-700"
            style={{
              backgroundColor: isCorrect === true ? "#1c6b45" : "#fff",
              color: isCorrect === true ? "#fff" : INK,
              border: "1px solid #1c6b45",
              borderRadius: 6,
              padding: "9px 20px",
            }}
            onClick={() => { setIsCorrect(true); setMisconception("") }}
          >
            Correct
          </button>
          <button
            type="button"
            className="btn fw-700"
            style={{
              backgroundColor: isCorrect === false ? ACCENT : "#fff",
              color: isCorrect === false ? "#fff" : INK,
              border: `1px solid ${ACCENT}`,
              borderRadius: 6,
              padding: "9px 20px",
            }}
            onClick={() => setIsCorrect(false)}
          >
            Not correct
          </button>
        </div>

        {/* Only asked for a wrong answer, and optional even then. Automatic marking never
            records a misconception on practice - there is no anticipated-wrong-answer table for
            the AQ bank - but a tutor reading the answer may see something worth noting, and
            that is more information rather than a re-diagnosis. */}
        {isCorrect === false && (
          <div className="mb-3">
            <label className="d-block fw-700 mb-1" style={{ color: INK, fontSize: 12.5 }}>
              What kind of error was it? <span style={{ color: MUTED }}>(optional)</span>
            </label>
            <select
              className="form-select"
              value={misconception}
              onChange={e => setMisconception(e.target.value)}
              style={{ maxWidth: 420, fontSize: 13 }}
            >
              <option value="">Not sure / none of these</option>
              {types.map(type => (
                <option key={type.code} value={type.code}>{type.label}</option>
              ))}
            </select>
            {misconception && (
              <div className="mt-2" style={{ color: MUTED, fontSize: 12 }}>
                {types.find(t => t.code === misconception)?.description}
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          className="btn fw-700"
          style={{
            backgroundColor: isCorrect === null ? "#e9ecf1" : ACCENT,
            color: isCorrect === null ? MUTED : "#fff",
            border: "none",
            borderRadius: 6,
            padding: "10px 24px",
          }}
          disabled={isCorrect === null || saving}
          onClick={() => void save()}
        >
          {saving ? "Saving…" : "Save and next"}
        </button>
      </div>
    </>,
  )
}

export default EngSkillBuilderMarkingPage
